/**
 * payableQty — the quantity of a pass that is PAID, where a person said it is
 * fewer than QC passed.
 *
 * Mikko, 2026-10-03, relaying HR: "add column Payable, this Payable is equal
 * to QC Passed, but this is editable column, so it can edit Qty for Payable."
 *
 * QC PASSED stays the gate. Payable starts equal to it and a person can bring
 * it DOWN — a set that passed and was damaged at the sewer, a short count, a
 * pass the subcontractor agreed to be paid part of. It is never raised above
 * what QC passed: a WIP nobody passed cannot be paid (the same rule as
 * passLookup), and a payable above the pass would invent sets BQC5 never saw.
 *
 * KEYED ON THE PASS, NOT THE WIP, for the reason keyOverrides.ts is: a WIP
 * inspected twice is two passes, and a later pass of the same WIP (next week's
 * rework coming back) is a different key — so an adjustment made for this
 * week's pass never quietly follows the WIP onto the next run. The screen shows
 * one row per WIP (wipMerge.ts) and asks for ONE number for it; `allocatePayable`
 * spreads that number over the row's passes, oldest first, and stores only the
 * passes that end up below what QC passed.
 *
 * Only an entry BELOW the pass's qty is ever stored. Typing the QC passed
 * figure back removes the entry, so "reset" is just typing the default.
 *
 * Stored as one JSON map in `iSyn2_SubconPayrollSettings` like the holds and
 * the pants types — no new list, no new column, no Migration Valve step. Pure.
 */

/** The settings-list key the stored quantities live under. */
export const KEY_PAYABLE_KEY = "keyPayable";

/** Pass key -> sets payable on it (always below what QC passed). */
export type StoredKeyPayable = Record<string, number>;

/** Parse the stored blob. Never throws; an unreadable row means nothing was changed. */
export function parseStoredKeyPayable(text: string | null | undefined): StoredKeyPayable {
  if (!text) return {};
  try {
    const raw: unknown = JSON.parse(text);
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const out: StoredKeyPayable = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      const key = String(k ?? "").trim();
      const n = Number(v);
      if (key && Number.isFinite(n) && n >= 0) out[key] = Math.floor(n);
    }
    return out;
  } catch {
    return {};
  }
}

/** What is payable on one pass: the stored figure, never above what QC passed. */
export function payableOf(stored: StoredKeyPayable | undefined, key: string, qcPassed: number): number {
  const v = stored?.[key];
  if (v === undefined) return qcPassed;
  return Math.max(0, Math.min(qcPassed, Math.floor(v)));
}

/** The passes behind one row, in the order the payable is taken from them. */
export interface IPayablePass { key: string; qty: number }

/**
 * Spread one typed number over a row's passes: each is filled up to its own
 * QC passed, in order, until the number runs out. The total is clamped to
 * 0 … Σ qty. Answers `{ key: payable }` for EVERY pass given, so the caller can
 * write the ones below default and clear the rest in one go.
 */
export function allocatePayable(passes: readonly IPayablePass[], total: number): Record<string, number> {
  const cap = passes.reduce((n, p) => n + p.qty, 0);
  let left = Math.max(0, Math.min(cap, Math.floor(Number.isFinite(total) ? total : cap)));
  const out: Record<string, number> = {};
  for (const p of passes) {
    const take = Math.min(p.qty, left);
    out[p.key] = take;
    left -= take;
  }
  return out;
}

/** The payable on a row's passes, summed. */
export function payableTotal(stored: StoredKeyPayable | undefined, passes: readonly IPayablePass[]): number {
  return passes.reduce((n, p) => n + payableOf(stored, p.key, p.qty), 0);
}

/**
 * Merge an allocation into the stored map: an entry below the pass's QC passed
 * is written, one equal to it is dropped (that is the default again). Returns
 * a new map; the input is untouched.
 */
export function mergePayable(
  stored: StoredKeyPayable, passes: readonly IPayablePass[], alloc: Readonly<Record<string, number>>,
): StoredKeyPayable {
  const next = { ...stored };
  for (const p of passes) {
    const v = alloc[p.key];
    if (v === undefined || v >= p.qty) delete next[p.key];
    else next[p.key] = Math.max(0, Math.floor(v));
  }
  return next;
}
